<template>
    <header
        class="ps-nav"
        :class="{
            'ps-nav--scrolled': isScrolled,
            'ps-nav--open': mobileOpen,
        }"
    >
        <div class="ps-nav__bar">
            <div class="container">
                <div class="ps-nav__inner">
                    <!-- Logo -->
                    <Link :href="route('home')" class="ps-nav__logo" aria-label="ProfileSuite home" @click="closeMobile">
                        <logo class="ps-nav__logo-img ps-nav__logo-img--dark" />
                        <logo name="white" class="ps-nav__logo-img ps-nav__logo-img--light" />
                    </Link>

                    <!-- Desktop navigation -->
                    <nav class="ps-nav__desktop" aria-label="Main navigation">
                        <ul class="ps-nav__links">
                            <li v-for="link in navLinks" :key="link.id">
                                <Link
                                    :href="link.href"
                                    class="ps-nav__link"
                                    :class="{ 'ps-nav__link--active': activeId === link.id }"
                                >
                                    {{ __(link.label) }}
                                </Link>
                            </li>
                            <li
                                class="ps-nav__dropdown-wrap"
                                @mouseenter="openTools"
                                @mouseleave="closeTools"
                            >
                                <button
                                    type="button"
                                    class="ps-nav__link ps-nav__link--trigger"
                                    :class="{ 'ps-nav__link--active': activeId === 'tools', 'ps-nav__link--open': toolsOpen }"
                                    aria-haspopup="true"
                                    :aria-expanded="toolsOpen ? 'true' : 'false'"
                                    @click="toolsOpen = !toolsOpen"
                                >
                                    {{ __('Tools') }}
                                    <svg class="ps-nav__chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                                <div v-show="toolsOpen" class="ps-nav__dropdown">
                                    <p class="ps-nav__dropdown-title">{{ __('Creator tools') }}</p>
                                    <ul class="ps-nav__dropdown-grid">
                                        <li v-for="tool in toolLinks" :key="tool.slug">
                                            <Link
                                                :href="route('tools.show', tool.slug)"
                                                class="ps-nav__dropdown-item"
                                                @click="toolsOpen = false"
                                            >
                                                <span class="ps-nav__dropdown-icon">
                                                    <icon :name="tool.icon" class="w-4 h-4" />
                                                </span>
                                                <span>
                                                    <span class="ps-nav__dropdown-name">{{ tool.name }}</span>
                                                    <span class="ps-nav__dropdown-desc">{{ tool.desc }}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    </ul>
                                    <Link
                                        :href="route('tools')"
                                        class="ps-nav__dropdown-footer"
                                        @click="toolsOpen = false"
                                    >
                                        {{ __('View all tools') }} →
                                    </Link>
                                </div>
                            </li>
                        </ul>
                    </nav>

                    <!-- Actions -->
                    <div class="ps-nav__actions">
                        <Link
                            :href="route('login')"
                            class="ps-nav__login hidden sm:inline-flex"
                        >
                            {{ __('Login') }}
                        </Link>
                        <Link
                            :href="route('tools.show', 'print-studio')"
                            class="ps-nav__cta hidden sm:inline-flex"
                        >
                            <icon name="print" class="w-4 h-4" />
                            {{ __('Get Started') }}
                        </Link>

                        <button
                            type="button"
                            class="ps-nav__toggle lg:hidden"
                            :aria-expanded="mobileOpen ? 'true' : 'false'"
                            aria-controls="ps-nav-mobile"
                            aria-label="Toggle menu"
                            @click="toggleMobile"
                        >
                            <span class="ps-nav__toggle-bar" />
                            <span class="ps-nav__toggle-bar" />
                            <span class="ps-nav__toggle-bar" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile overlay -->
        <div
            v-show="mobileOpen"
            class="ps-nav__overlay lg:hidden"
            aria-hidden="true"
            @click="closeMobile"
        />

        <!-- Mobile drawer -->
        <div
            id="ps-nav-mobile"
            class="ps-nav__mobile lg:hidden"
            :class="{ 'ps-nav__mobile--open': mobileOpen }"
        >
            <div class="ps-nav__mobile-inner">
                <nav aria-label="Mobile navigation">
                    <ul class="ps-nav__mobile-links">
                        <li v-for="link in navLinks" :key="'m-' + link.id">
                            <Link
                                :href="link.href"
                                class="ps-nav__mobile-link"
                                :class="{ 'ps-nav__mobile-link--active': activeId === link.id }"
                                @click="closeMobile"
                            >
                                {{ __(link.label) }}
                            </Link>
                        </li>
                    </ul>

                    <p class="ps-nav__mobile-section">{{ __('Tools') }}</p>
                    <ul class="ps-nav__mobile-tools">
                        <li v-for="tool in toolLinks" :key="'mt-' + tool.slug">
                            <Link
                                :href="route('tools.show', tool.slug)"
                                class="ps-nav__mobile-tool"
                                @click="closeMobile"
                            >
                                <icon :name="tool.icon" class="w-4 h-4 shrink-0" />
                                {{ tool.name }}
                            </Link>
                        </li>
                    </ul>
                </nav>

                <div class="ps-nav__mobile-actions">
                    <Link :href="route('login')" class="ps-nav__mobile-login" @click="closeMobile">
                        {{ __('Login') }}
                    </Link>
                    <Link
                        :href="route('tools.show', 'print-studio')"
                        class="ps-nav__mobile-cta"
                        @click="closeMobile"
                    >
                        {{ __('Open Print Studio') }}
                    </Link>
                </div>
            </div>
        </div>
    </header>
</template>

<script>
import Logo from '@/Shared/Logo'
import Icon from '@/Shared/Icon'
import { Link } from '@inertiajs/vue3'

export default {
    components: {
        Logo,
        Icon,
        Link,
    },
    data() {
        return {
            isScrolled: false,
            mobileOpen: false,
            toolsOpen: false,
            toolsCloseTimer: null,
        }
    },
    computed: {
        navLinks() {
            return [
                { id: 'home', label: 'Home', href: this.route('home') },
                { id: 'faq', label: 'FAQ', href: this.route('faq') },
                { id: 'blog', label: 'Blog', href: this.route('blog') },
                { id: 'contact', label: 'Contact', href: this.route('contact') },
            ]
        },
        toolLinks() {
            return [
                { slug: 'print-studio', name: 'Print Studio', icon: 'print', desc: 'Print-ready cards' },
                { slug: 'business-card', name: 'Business Card', icon: 'card', desc: 'Digital shareable card' },
                { slug: 'link-in-bio', name: 'Link-in-Bio', icon: 'website', desc: 'All links in one page' },
                { slug: 'landing-page', name: 'Landing Page', icon: 'page', desc: 'Conversion pages' },
                { slug: 'lead-form', name: 'Lead Capture', icon: 'contact', desc: 'Smart lead forms' },
                { slug: 'resume', name: 'Resume / CV', icon: 'file', desc: 'Online resume builder' },
            ]
        },
        activeId() {
            const segment = this.$page.url.replace(/^\//, '').split('/')[0] || 'home'
            if (segment === 'tools' || segment.startsWith('t')) {
                return 'tools'
            }
            const match = this.navLinks.find((l) => l.id === segment)
            return match ? match.id : segment
        },
    },
    watch: {
        '$page.url'() {
            this.closeMobile()
            this.toolsOpen = false
        },
    },
    methods: {
        openTools() {
            if (this.toolsCloseTimer) {
                clearTimeout(this.toolsCloseTimer)
                this.toolsCloseTimer = null
            }
            this.toolsOpen = true
        },
        closeTools() {
            this.toolsCloseTimer = setTimeout(() => {
                this.toolsOpen = false
            }, 120)
        },
        onScroll() {
            this.isScrolled = window.scrollY > 24
        },
        toggleMobile() {
            this.mobileOpen = !this.mobileOpen
            document.body.style.overflow = this.mobileOpen ? 'hidden' : ''
        },
        closeMobile() {
            this.mobileOpen = false
            document.body.style.overflow = ''
            if (this.toolsCloseTimer) {
                clearTimeout(this.toolsCloseTimer)
                this.toolsCloseTimer = null
            }
            this.toolsOpen = false
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.closeMobile()
                this.toolsOpen = false
            }
        },
    },
    mounted() {
        this.onScroll()
        window.addEventListener('scroll', this.onScroll, { passive: true })
        window.addEventListener('keydown', this.onKeydown)
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.onScroll)
        window.removeEventListener('keydown', this.onKeydown)
        document.body.style.overflow = ''
        if (this.toolsCloseTimer) {
            clearTimeout(this.toolsCloseTimer)
        }
    },
}
</script>
